import {ChangeDetectorRef, Component, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {LocationMessage, NodeBuilder} from '../../../../../../../shared/models';
import {additionalDataErrorMessage, SendTemplateNode, TemplateForm} from './sendTemplate';
import {SendTemplateService, WaBusinessAccounts, WaIntegrations} from './send-template.service';
import {switchMap, tap} from 'rxjs/operators';
import {WaTemplate} from '../../../../../template/wa-template-preview/wa-template.service';
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import isEmpty from 'lodash/isEmpty';
import {MatAutocomplete} from '@angular/material/autocomplete';
import {MatButtonToggleGroup} from '@angular/material/button-toggle';
import {additionalDataMaxLength, isValidVariable, required} from '../../../../../../../shared/utils';
import {MatOption} from '@angular/material/core';
import {SharedService} from '../../../../../../../core/services/store/shared-store/shared-storage.service';
import {BehaviorSubject, Subscription} from 'rxjs';

const NODE_TYPE = 'sendTemplate';

@Component({
    selector: 'app-send-template',
    templateUrl: './send-template.component.html',
    styleUrls: ['./send-template.component.scss']
})

export class SendTemplateComponent extends NodeBuilder implements OnInit, DoCheck, OnDestroy {

    actionParamsForm: FormGroup = undefined;
    templateContentForm: FormGroup = undefined;

    waBusinessAccounts: WaBusinessAccounts[] = undefined;
    waIntegrations: WaIntegrations[] = undefined;
    waTemplates: WaTemplate[] = undefined;

    waBusinessAccountsFiltered: WaBusinessAccounts[] = [];
    waIntegrationsFiltered: WaIntegrations[] = [];
    waTemplatesFiltered: WaTemplate[] = [];

    waIntegrationsLoading = false;
    waTemplatesLoading = false;
    template: WaTemplate = undefined;
    templateIdCheck: string = undefined;

    loadInitData = false;


    stepUniqueID = '';
    selectedMediaType: 'assets' | 'variable' = 'assets';
    private nameValueChanges$: Subscription;

    isLocationPicker = false;

    get getBodyVars(): FormArray {
        return this.templateContentForm.controls['bodyVars'] as FormArray;
    }

    get getWebsiteUrls(): FormArray {
        return this.templateContentForm.controls['websiteUrls'] as FormArray;
    }

    get businessAccountId(): FormControl {
        return this.actionParamsForm.controls['businessAccountId'] as FormControl;
    }

    get integrationId(): FormControl {
        return this.actionParamsForm.controls['integrationId'] as FormControl;
    }

    get templateId(): FormControl {
        return this.actionParamsForm.controls['templateId'] as FormControl;
    }


    constructor(private sendTemplateService: SendTemplateService, private sharedService: SharedService, private changeDetectorRef: ChangeDetectorRef) {
        super(NODE_TYPE);
    }

    ngOnInit(): void {
        this.sendTemplateService.get_accounts().pipe(
            tap(data => this.waBusinessAccounts = data),
            tap(data => this.waBusinessAccountsFiltered = data.slice())
        ).subscribe();
    }

    ngDoCheck(): void {
    }

    ngOnDestroy(): void {
        this.nameValueChanges$?.unsubscribe();
    }

    public wabaFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waBusinessAccounts)) {
            this.waBusinessAccountsFiltered = [];
            return;
        }
        this.clearWaIntegrations();
        this.waBusinessAccountsFiltered = this.waBusinessAccounts?.filter(option => option.wabaName.includes(filterValue));
    }

    public waIntegrationsFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waIntegrations)) {
            this.waIntegrationsFiltered = [];
            return;
        }
        this.clearWaTemplate();
        this.waIntegrationsFiltered = this.waIntegrations?.filter(option => option.integrationName.includes(filterValue));
    }

    public waTemplatesFilter(element: HTMLInputElement) {
        const filterValue = element.value;
        if (isEmpty(this.waTemplates)) {
            this.waTemplatesFiltered = [];
            return;
        }
        this.waTemplatesFiltered = this.waTemplates?.filter(option => option.name.includes(filterValue));
    }


    set buildForm(stepData: any) {
        const {actionParamsForm, templateContentForm} = SendTemplateNode.toForm(stepData, this.canEdit);
        this.actionParamsForm = actionParamsForm;

        if (this.actionParamsForm.get('success').value === newStepValue) {
            this.actionParamsForm.get('success').setValue('step' + this.getNextStep());
        }

        if (this.actionParamsForm.get('error').value === newStepValue) {
            this.actionParamsForm.get('error').setValue('step' + this.getNextStep());
        }

        this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';

        if (!isEmpty(stepData)) {
            this.loadInitialData(templateContentForm, stepData);
            this.setMediaType();
        }

    }

    private loadInitialData(templateContentForm: TemplateForm, stepData) {
        const businessAccountId = this.businessAccountId.value;
        const integrationId = this.integrationId.value;

        this.waIntegrationsLoading = true;
        this.waTemplatesLoading = true;

        this.loadInitData = true;


        this.sendTemplateService.get_integrations(businessAccountId).pipe(
            switchMap(waIntegrations => {
                this.waIntegrations = waIntegrations;
                this.waIntegrationsFiltered = waIntegrations.slice();
                return this.sendTemplateService.get_templates(integrationId).pipe(
                    tap(data => this.waTemplates = data),
                    tap(data => this.waTemplatesFiltered = data.slice())
                );
            })
        ).subscribe(() => {
            this.waIntegrationsLoading = false;
            this.waTemplatesLoading = false;
            this.template = this.waTemplates.find(x => x.id === this.templateId.value);
            this.templateIdCheck = this.templateId.value;

            const templateFormData = SendTemplateNode.toTemplate(this.template);
            this.templateContentForm = SendTemplateNode.buildTemplate(this.canEdit, templateFormData);
            this.templateContentForm.patchValue(templateContentForm);

            if (this.templateContentForm.controls['suggestedResponse'] &&
                !isEmpty(stepData) && stepData['action']['actionParams']['parameters']['buttons'] !== undefined &&
                stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse'] !== undefined) {

                Object.keys(stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse']).forEach(key => {
                    this.templateContentForm.controls['suggestedResponse'].get(key).addValidators(
                        additionalDataMaxLength(this.actionParamsForm.get('name'), this.sharedService.getEntityId(), additionalDataErrorMessage));
                });

                this.nameValueChanges$?.unsubscribe();
                this.nameValueChanges$ = this.actionParamsForm.get('name').valueChanges.pipe(
                    tap(() => {
                        Object.keys(stepData['action']['actionParams']['parameters']['buttons']['suggestedResponse']).forEach(key => {
                            this.templateContentForm.controls['suggestedResponse'].get(key).updateValueAndValidity();
                        });
                    })
                ).subscribe();
            }

            // This is a semi **** fix
            this.loadInitData = false;
        });
    }

    get getStepData(): {} {
        const stepData = new SendTemplateNode();
        stepData.addActionParams(this.actionParamsForm.getRawValue());
        if (this.templateContentForm) {
            stepData.addTemplateParameters(this.templateContentForm.getRawValue());
        }

        const nodeData = stepData.nodeData;
        if (this.stepUniqueID !== '') {
            nodeData['metadata'].stepUniqueID = this.stepUniqueID;
        }
        return nodeData;
    }

    get isNodeValid(): boolean {

        this.isValidControl('integration', this.integrationId, this.findWaIntegrations);
        this.isValidControl('template', this.templateId, this.findWaTemplate);
        this.isValidControl('business account', this.businessAccountId, this.findWaBusinessAccounts);

        this.actionParamsForm.markAllAsTouched();

        if ( !this.templateContentForm) {
            return false;
        }

        this.templateContentForm.markAllAsTouched();
        return this.templateContentForm.valid && this.actionParamsForm.valid;
    }

    waBusinessAccountsChange(id: string) {

        if (isEmpty(id)) {
            return;
        }

        this.waIntegrationsLoading = true;
        this.clearWaIntegrations();

        this.sendTemplateService.get_integrations(id).pipe(
            tap(data => this.waIntegrations = data),
            tap(data => this.waIntegrationsFiltered = data.slice())
        ).subscribe(() => this.waIntegrationsLoading = false);
    }

    waIntegrationsChange(id: string) {

        if (isEmpty(id)) {
            return;
        }

        this.waTemplatesLoading = true;
        this.clearWaTemplate();

        this.sendTemplateService.get_templates(id).pipe(
            tap(data => this.waTemplates = data),
            tap(data => this.waTemplatesFiltered = data.slice())
        ).subscribe(() => this.waTemplatesLoading = false);
    }

    waTemplatesChange(id: string) {
        if (isEmpty(id) || this.templateIdCheck === id) {
            return;
        }
        this.templateIdCheck = id;
        this.templateContentForm = undefined;
        this.changeDetectorRef.detectChanges();
        this.template = this.waTemplates.find(x => x.id === id);
        const templateFormData = SendTemplateNode.toTemplate(this.template);
        this.templateContentForm = SendTemplateNode.buildTemplate(this.canEdit, templateFormData);

        if (this.template.templateContent.buttons && this.template.templateContent.buttons.quickReply) {
            for (let i = 1; i <= this.template.templateContent.buttons.quickReply.length; i++) {
                this.templateContentForm.controls['suggestedResponse'].get(i.toString()).addValidators(
                    additionalDataMaxLength(this.actionParamsForm.get('name'), this.sharedService.getEntityId(), additionalDataErrorMessage));
            }
        }

        this.setMediaType();

        // will handle new templates/nodes
        if (this.template.templateContent.buttons && this.template.templateContent.buttons.quickReply) {
            this.nameValueChanges$?.unsubscribe();
            this.nameValueChanges$ = this.actionParamsForm.get('name').valueChanges.pipe(
                tap(() => {
                    for (let i = 1; i <= this.template.templateContent.buttons.quickReply.length; i++) {
                        this.templateContentForm.controls['suggestedResponse'].get(i.toString()).updateValueAndValidity();
                    }
                })
            ).subscribe();
        }

    }

    waBusinessAccountsDisplay(id: string): string {
        if (this.waBusinessAccounts && id) {
            return this.findWaBusinessAccounts(id).wabaName;
        }

        return '';
    }

    waIntegrationsDisplay(id: string): string {
        if (this.waIntegrations && id) {
            return this.findWaIntegrations(id).integrationName;
        }

        return '';
    }

    waTemplatesDisplay(id: string): string {
        if (this.waTemplates && id) {
            const template: WaTemplate = this.findWaTemplate(id);

            return `(${template.language?.shortName}) ${template.name}`;
        }

        return '';
    }

    private isValidControl(name: string, control: FormControl, find: Function) {
        const templateData: any = find.apply(this, [control.value]);
        const addInvalidError = !control?.errors?.required;
        if (isEmpty(templateData) && addInvalidError) {
            control.setErrors({'invalidId': `Invalid ${name}`, ...control.errors});
            return;
        }

        if (control.hasError('invalidId')) {
            delete control.errors['invalidId'];
        }
    }

    private findWaBusinessAccounts(id: string): WaBusinessAccounts {
        return this.waBusinessAccounts?.find(x => x.id === id);
    }

    private findWaIntegrations(id: string): WaIntegrations {
        return this.waIntegrations?.find(x => x.integrationId === id);
    }

    private findWaTemplate(id: string): WaTemplate {
        return this.waTemplates?.find(x => x.id === id);
    }

    public setImage(event: any): void {
        this.templateContentForm?.get('mediaVar').setValue(event);
    }

    public onFocusIn(element: HTMLDivElement, auto: MatAutocomplete, controlName: string) {

        switch (controlName) {
            case 'businessAccountId':
                this.focusAccountDropdown(auto);
                break;
            case 'integrationId':
                this.focusIntegrationDropdown(auto);
                break;
            case 'templateId':
                this.focusTemplateDropdown(auto);
                break;
        }

        // prevent node form scrolling when dropdown open
        element.style.overflowY = 'hidden';
    }

    private focusAccountDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('businessAccountId').value.length) {
            const foundId = this.actionParamsForm.get('businessAccountId').value;
            const foundAccount = this.waBusinessAccountsFiltered.find(account => account.id === foundId);
            const accountIndex = this.waBusinessAccountsFiltered.indexOf(foundAccount);
            this.setDropdownScroll(auto, accountIndex);
        }
    }

    private focusIntegrationDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('integrationId').value.length) {
            const foundIntegrationId = this.actionParamsForm.get('integrationId').value;
            const foundIntegration = this.waIntegrationsFiltered.find(integration => integration.integrationId === foundIntegrationId);
            const integrationIndex = this.waIntegrationsFiltered.indexOf(foundIntegration);
            this.setDropdownScroll(auto, integrationIndex);
        }
    }

    private focusTemplateDropdown(auto: MatAutocomplete) {
        if (this.actionParamsForm.get('templateId').value.length) {
            const foundTemplateId = this.actionParamsForm.get('templateId').value;
            const foundTemplate = this.waTemplatesFiltered.find(template => template.id === foundTemplateId);
            const templateIndex = this.waTemplatesFiltered.indexOf(foundTemplate);
            this.setDropdownScroll(auto, templateIndex);
        }
    }

    private setDropdownScroll(auto: MatAutocomplete, accountIndex: number) {
        const matOptionHeight = 48;
        const matOption = auto.options.get(accountIndex) as MatOption;

        setTimeout(() => {
            matOption.focus();

            // matOption.focus() doesnt always scroll to the correct position
            auto._setScrollTop(matOptionHeight * accountIndex);
        }, 0);
    }

    public onFocusOut(element: HTMLDivElement) {
        // prevent node form scrolling when dropdown open
        element.style.overflowY = 'auto';
    }

    private setMediaType(): void {
        if (this.templateContentForm) {
            const control = this.templateContentForm.get('mediaVar');
            if (control) {
                const val: string = control.value;
                if (val.startsWith('[[') && val.endsWith(']]')) {
                    this.selectedMediaType = 'variable';
                    return;
                }
            }
        }
        this.selectedMediaType = 'assets';
    }

    public mediaTypeChange(event: MatButtonToggleGroup): void {
        if (this.templateContentForm.get('mediaVar')) {
            this.templateContentForm.get('mediaVar').patchValue('');
            if (event.value === 'variable') {
                this.templateContentForm.get('mediaVar').setValidators([isValidVariable(), required('Media')]);
            } else {
                this.templateContentForm.get('mediaVar').setValidators([required('Media')]);
            }
        }
        this.selectedMediaType = event.value;
    }

    public clearWaIntegrations(): void {
        this.integrationId.setValue('');
        this.templateId.setValue('');

        this.waTemplates = [];
        this.waIntegrations = [];

        this.waTemplatesFiltered = [];
        this.waIntegrationsFiltered = [];
    }

    public clearWaTemplate(): void {
        this.templateId.setValue('');

        this.waTemplates = [];
        this.waTemplatesFiltered = [];
    }

    setLocation(locationMessage: LocationMessage) {

        const location = this.templateContentForm.get('location');

        location.get('locationName').setValue(locationMessage.locationName);
        location.get('address').setValue(locationMessage.locationAddress);
        location.get('latitude').setValue(locationMessage.latitude);
        location.get('longitude').setValue(locationMessage.longitude);
    }

    closeLocation(event) {
        this.isLocationPicker = false;
    }

    showLocationPicker() {
        this.isLocationPicker = !this.isLocationPicker;
    }

}
